import { Stepper } from "@closer/ui";

export interface ContractLifecycleStripState {
  code: string;
  label: string;
}

export interface ContractLifecycleStripProps {
  /**
   * Durumlar SUNUCUNUN kanonik sırasıyla (`/api/contracts/:id/lifecycle` → `states`).
   *
   * ⚠ Sıra burada TANIMLANMIYOR, yalnız çiziliyor. Ekranda ikinci bir durum listesi
   * tutsaydık sunucu akışa bir adım eklediğinde şerit sessizce eski sırayı göstermeye
   * devam ederdi (kriter 6 — bilgi tek yerde yaşar).
   */
  states: readonly ContractLifecycleStripState[];
  /** Sözleşmenin şu anki durumu (`lifecycle` → `status`). */
  status: string;
}

/**
 * Sözleşme yaşam döngüsü şeridi (sunum turu, 4 Eylül 2026).
 *
 * ⚠ NEDEN: detay ekranı süreçleri (inceleme, iç onay, mutabakat, imza) eşit ağırlıkta
 * kartlar hâlinde sıralıyor ve her biri ayrı ayrı "henüz başlatılmadı" diyordu. Sürecin
 * bir SIRASI olduğu hiçbir yerde yazmadığı için kullanıcı hangi adımda olduğunu ve
 * sıradakinin ne olduğunu bilmiyordu — canlı turda yeni yüklenen bir sözleşmede
 * ölçüldü.
 *
 * ⚠ `Stepper`'ın "sahte adım yasak" kuralı burada karşılanıyor: akış gerçekten çok
 * adımlı ve adımların kaynağı sunucu, ekranın kendi uydurması değil.
 */
export function ContractLifecycleStrip({ states, status }: ContractLifecycleStripProps) {
  const sira = states.findIndex((durum) => durum.code === status);

  /**
   * ⚠ Bilinmeyen durumda şerit HİÇ çizilmiyor. `findIndex` -1 döndüğünde `Stepper` hiçbir
   * adımı aktif saymaz ve şerit sözleşmeyi en baştaymış gibi gösterirdi; kullanıcıya
   * yanlış bir aşama söylemektense aşamayı hiç söylememek doğru davranış.
   */
  if (sira < 0) return null;

  return (
    /**
     * ⚠ Yatay kaydırma: akış on duruma kadar çıkabiliyor (`expired`, `terminated`,
     * `archived` dahil) ve dar ekranda adım etiketleri birbirine giriyordu. Şerit
     * kırpılmıyor — kırpılan bir akış, olmayan bir sonu varmış gibi gösterir.
     */
    <div data-testid="yasam-dongusu-seridi" className="overflow-x-auto pb-1">
      <Stepper steps={states.map((durum) => durum.label)} current={sira} className="min-w-max" />
    </div>
  );
}

/**
 * Yaşam döngüsünün KOMPAKT hâli — başlık satırında duran tek çip.
 *
 * ### ⚠ Tam şeridin yerine geçmiyor, onu SERBEST BIRAKIYOR
 *
 * 4 Eylül turunun kararı şuydu: *"sözleşmenin hangi aşamada olduğu bir bölümün detayı
 * değil, kaydın kimliği — hangi sekmeye bakılırsa bakılsın görünmeli."* Karar geçerli;
 * değişen yalnız o gerekliliğin KAÇ PİKSELE mal olduğu.
 *
 * On adımlı şerit her bölümün üstünde ~65px yiyordu ve ölçüldü (5 Eylül): 1600×1000
 * ekranda içerik 429. pikselde başlıyordu, yani ekranın %43'ü içeriğe hiç ayrılmıyordu.
 * Konsept tasarımın aynı noktası 258. piksel. Farkın en büyük iki kalemi bu şerit ve üst
 * özet satırıydı.
 *
 * Çip, kararın İSTEDİĞİNİ veriyor (aşama her sekmede görünür) ve tam şerit — adımların
 * sırasının okunması gereken tek yer — Süreç bölümünde duruyor.
 *
 * ⚠ Durum ADI burada TEKRAR EDİLMİYOR: hemen yanındaki rozet zaten "Aktif" yazıyor. Çip
 * yalnız o durumun akıştaki YERİNİ söylüyor; ikisini de yazmak aynı bilgiyi iki kez
 * göstermek olurdu.
 */
export function ContractLifecyclePill({ states, status }: ContractLifecycleStripProps) {
  const sira = states.findIndex((durum) => durum.code === status);

  /** Bilinmeyen durumda çip de çizilmiyor — tam şeritle aynı gerekçe. */
  if (sira < 0) return null;

  return (
    <span
      data-testid="yasam-dongusu-cipi"
      className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-fg-muted"
      title={`${states.length} aşamalı akışın ${sira + 1}. adımı`}
    >
      Aşama {sira + 1}/{states.length}
    </span>
  );
}
